import { useState } from 'react';
import { Icon, SignaturePad } from './ui';

/**
 * The three signing steps shared by the in-app flow and the public link flow:
 * read → consent + signature → (confirm). The caller supplies the final submit.
 */
export function SignFlow({ signerName, body, onOpenFile, onSubmit, onCancel, extraStep }: {
  signerName: string; body: string | null; onOpenFile?: () => void;
  onSubmit: (s: { typedName: string | null; signatureImage: string | null; consent: true }) => Promise<void>;
  onCancel: () => void; extraStep?: string;
}) {
  const [step, setStep] = useState(1);
  const [read, setRead] = useState(false);
  const [consent, setConsent] = useState(false);
  const [mode, setMode] = useState<'type' | 'draw'>('type');
  const [typed, setTyped] = useState('');
  const [img, setImg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const norm = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();
  const sigOk = mode === 'type' ? norm(typed) === norm(signerName) : !!img;

  const submit = async () => {
    setBusy(true); setErr(null);
    try { await onSubmit({ typedName: mode === 'type' ? typed : null, signatureImage: mode === 'draw' ? img : null, consent: true }); setStep(3); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <div className="stack">
      <ol className="sign-steps">{['Read', 'Sign', 'Done'].map((s, i) => <li key={s} className={i + 1 < step ? 'done' : i + 1 === step ? 'now' : ''}><span>{i + 1 < step ? <Icon n="check" s={11} /> : i + 1}</span>{s}</li>)}</ol>
      {step === 1 && (
        <>
          {body ? <div className="paper scroll"><div className="paper-body">{body.split('\n').filter(Boolean).map((t, i) => <p key={i}>{t}</p>)}</div></div>
            : <div className="callout subtle small"><Icon n="doc" s={16} /> <span>Open the PDF and read every page before signing. <button className="link" onClick={onOpenFile}>Open the document</button></span></div>}
          <label className="check" htmlFor="chk-read"><input id="chk-read" type="checkbox" checked={read} onChange={(e) => setRead(e.target.checked)} /> I have read the whole document.</label>
          <div className="row gap"><button className="btn primary" disabled={!read} onClick={() => setStep(2)}>Continue</button><button className="btn" onClick={onCancel}>Not now</button></div>
        </>
      )}
      {step === 2 && (
        <>
          <label className="check" htmlFor="chk-consent"><input id="chk-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} /> I agree to sign this document electronically and understand my electronic signature has the same effect as a handwritten one.</label>
          <div className="seg" role="radiogroup" aria-label="Signature type">
            <button role="radio" aria-checked={mode === 'type'} className={mode === 'type' ? 'on' : ''} onClick={() => setMode('type')}>Type my name</button>
            <button role="radio" aria-checked={mode === 'draw'} className={mode === 'draw' ? 'on' : ''} onClick={() => setMode('draw')}>Draw signature</button>
          </div>
          {mode === 'type' ? (
            <label className="field" htmlFor="sig-typed"><span>Type your full name exactly: <em>{signerName}</em></span>
              <input id="sig-typed" className="sig-input" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
              {typed && <span className="sig-typed big">{typed}</span>}
            </label>
          ) : <SignaturePad onChange={setImg} />}
          {extraStep && <p className="small dim">{extraStep}</p>}
          {err && <div className="callout bad small" role="alert">{err}</div>}
          <div className="row gap"><button className="btn primary" disabled={!consent || !sigOk || busy} onClick={submit}>{busy ? 'Signing…' : 'Sign document'}</button><button className="btn" onClick={() => setStep(1)}>Back</button></div>
        </>
      )}
      {step === 3 && (
        <div className="stack done-box">
          <Icon n="check" s={32} /><h3>Signed</h3>
          <p>Thank you. Anyone who still needs to sign has been notified, and you will get the signed copy by email.</p>
          <button className="btn primary" onClick={onCancel}>Close</button>
        </div>
      )}
    </div>
  );
}
